import { useStore } from "vuex";
import { reactive, computed, getCurrentInstance } from "vue";
import _ from "lodash/lang";

import useDom from "@/client/extensions/composition/useDom";
import { lcFirst } from "@/client/utilities";

let sanitizeUrl = require("@braintree/sanitize-url").sanitizeUrl;
export default (props, options = {}) => {
  const instance = getCurrentInstance();

  // default options
  if (!props || typeof props !== "object") {
    props = {};
  }

  if (!options || typeof options !== "object") {
    props = {};
  }

  let input = props.input && typeof props.input === "object" ? props.input : {};
  let inputPrefix = props.inputPrefix ? props.inputPrefix : false;
  let store = options.store ? options.store : useStore();
  let breakPoint = (
    options.breakPoint ? options.breakPoint : "l"
  ).toUpperCase();
  let isSmallPropertyName = "isSmallerThan" + breakPoint;

  let domUtils = useDom();
  let isSmall = domUtils[isSmallPropertyName];
  if (!isSmall) {
    warn(
      "error - can not find isSmall computed property. Most likely because options.breakPoint is invalid",
      { options, domUtils }
    );
    isSmall = domUtils.isSmallerThanL;
  }

  let getTrackingQueryFromUrl = () => {
    if (utilities.isSSR()) {
      return "";
    }

    if (!config.uiElements.overloadLinksWithTrackingParams) {
      return "";
    }

    let queryObject = {};
    const urlParams = new URLSearchParams(window.location.search);

    config.uiElements.overloadTrackingParamsList.forEach((key) => {
      let value = urlParams.get(key);

      if (value === null || typeof value === "undefined") {
        return true;
      }

      queryObject[key] = value;
    });
    let final = [];

    for (const [key, value] of Object.entries(queryObject)) {
      final.push(`${key}=${value}`);
    }
    final = final.join("&");

    return final;
  };
  let addNavigationHandler = (obj) => {
    let hasLink =
      utilities.isNonEmptyString(obj.href) &&
      (obj.action === "_blank" ||
        obj.action === "_current" ||
        obj.action === "_self");
    let hasEmit =
      !hasLink && obj.action !== "none" && obj.action !== "" && obj.action;

    if (hasLink && obj.paramForwardingOptOut !== true) {
      let trackingQuery = getTrackingQueryFromUrl();

      // this code ignores # and jsut adds in the end
      obj.finalHref += obj.finalHref.includes("?") ? "&" : "?";
      obj.finalHref += trackingQuery;

      // if this is an SPA or deep link - overload the url itself with the params
      if (obj.finalHref.includes("/#")) {
        let parts = obj.finalHref.split("/#");
        parts[0] += parts[0].includes("?") ? "&" : "?";
        parts[0] += trackingQuery;
        obj.finalHref = parts.join("/#");
      }
    }

    obj.navigationHandler = (e) => {
      if (!e) {
        return;
      }
      if (hasLink) {
        if (utilities.isSSR()) {
          return;
        }
        try {
          e.preventDefault();
        } catch (e) {}

        if (obj.target === "_blank") {
          window.open(sanitizeUrl(obj.finalHref));
        } else {
          window.location.href = sanitizeUrl(obj.finalHref);
        }
      } else {
        try {
          e.preventDefault();
        } catch (e) {}

        try {
          if (hasEmit) {
            let emit = instance?.ctx?.$emit;
            let component = instance.ctx;
            if (!emit) {
              emit = instance.emit;
              component = instance;
            }
            emit(obj.action, {
              component: component,
              uiElement: obj,
              event: obj.action,
            });
          }
        } catch (e) {
          warn(
            "failed to emit custom action from useUiElements, probably failed to get instance at setup",
            { obj, action: obj.action, instance }
          );
        }
      }
    };
  };
  // ui element map - hard coded for now
  // order matters! go from the more "demanding" element to the least demanding in terms of field requirements.
  let uiElementMap = {
    linkableImage: {
      fields: ["src", "srcMobile", "href", "action", "alt"],
      wrap: (obj) => {
        let hasLink =
          utilities.isNonEmptyString(obj.href) &&
          (obj.action === "_blank" ||
            obj.action === "_current" ||
            obj.action === "_self");

        obj.isSmall = isSmall;
        obj.hasLink = hasLink;
        obj.finalHref = hasLink ? obj.href : "#";
        obj.finalSrc = computed(() => {
          if (isSmall.value) {
            return utilities.isNonEmptyString(obj.srcMobile)
              ? obj.srcMobile
              : obj.src;
          } else {
            return obj.src;
          }
        });
        obj.target = hasLink ? obj.action : "_current";
        obj.show = !!obj.src;
        addNavigationHandler(obj);
        return obj;
      },
    },
    image: {
      fields: ["src", "srcMobile", "alt"],
      wrap: (obj) => {
        obj.isSmall = isSmall;
        obj.finalSrc = computed(() => {
          if (isSmall.value) {
            return utilities.isNonEmptyString(obj.srcMobile)
              ? obj.srcMobile
              : obj.src;
          } else {
            return obj.src;
          }
        });
        obj.show = !!obj.src;
        return obj;
      },
    },
    button: {
      fields: ["text", "theme", "size", "icon", "iconEnd", "href", "action"],
      wrap: (obj) => {
        let hasLink =
          utilities.isNonEmptyString(obj.href) &&
          (obj.action === "_blank" ||
            obj.action === "_current" ||
            obj.action === "_self");

        let hasText = utilities.isNonEmptyString(obj.text);
        let hasIcon = utilities.isNonEmptyString(obj.icon);
        let hasIconEnd = utilities.isNonEmptyString(obj.iconEnd);
        let hasTheme = utilities.isNonEmptyString(obj.theme);
        let hasSize = utilities.isNonEmptyString(obj.size);
        obj.target = hasLink ? obj.action : "_current";
        obj.finalHref = hasLink ? obj.href : "#";
        obj.hasLink = hasLink;
        obj.show = hasText || hasIcon || hasIconEnd;
        if (!hasSize) {
          obj.size = "m";
        }

        if (!hasTheme) {
          obj.theme = config.style.weUi.button.defaultTheme;
        }

        addNavigationHandler(obj);
        return obj;
      },
    },
    iconLink: {
      fields: ["text", "href", "icon", "action", "theme", "iconTheme"],
      wrap: (obj) => {
        let hasLink =
          utilities.isNonEmptyString(obj.href) &&
          (obj.action === "_blank" ||
            obj.action === "_current" ||
            obj.action === "_self");

        obj.show = computed(() => {
          return (
            utilities.isNonEmptyString(obj.text) ||
            utilities.isNonEmptyString(obj.icon)
          );
        });

        // hasText
        //finalHref
        obj.finalHref = hasLink ? obj.href : "#";
        obj.finalIcon = utilities.isNonEmptyString(obj.icon) ? obj.icon : false;
        obj.hasText = utilities.isNonEmptyString(obj.text);
        obj.target = hasLink ? obj.action : "_current";
        obj.hasLink = hasLink;

        addNavigationHandler(obj);

        return obj;
      },
    },
    link: {
      fields: ["href", "action", "text"],
      wrap: (obj) => {
        let hasLink =
          utilities.isNonEmptyString(obj.href) &&
          (obj.action === "_blank" ||
            obj.action === "_current" ||
            obj.action === "_self");

        obj.hasLink = hasLink;
        obj.finalHref = hasLink ? obj.href : "#";
        obj.show = utilities.isNonEmptyString(obj.text);
        obj.target = hasLink ? obj.action : "_current";

        addNavigationHandler(obj);
        return obj;
      },
    },
  };

  const removeInputAttributePrefix = (str) => {
    let subStringToRemove = inputPrefix;
    if (subStringToRemove) {
      const index = str.indexOf(subStringToRemove);
      if (index !== -1) {
        str = str.slice(0, index) + str.slice(index + subStringToRemove.length);
      }
    }

    return utilities.lcFirst(str);
  };

  const wrapUiElement = (obj) => {
    let uiElementType = getUiElementType(obj);
    if (!uiElementType) {
      return obj;
    }
    try {
      // already wrapped
      if (
        obj.hasOwnProperty("isUiElementWrapped") &&
        obj.isUiElementWrapped === true
      ) {
        return obj;
      }
      // wrap element
      let res = uiElementMap[uiElementType].wrap(obj);
      res.isUiElementWrapped = true;
      return res;
    } catch (e) {
      warn("failed to wap ui element", { uiElementType, value: obj });
      console.log(e);
      return obj;
    }
  };

  const wrapUiElements = (obj) => {
    for (const [key, val] of Object.entries(obj)) {
      obj[key] = wrapUiElement(val);
    }
  };

  const getUiElementType = (obj) => {
    if (!obj || typeof obj !== "object") {
      return false;
    }

    for (const [elementName, elementConfig] of Object.entries(uiElementMap)) {
      // strinct match
      // if (arraysHaveSameValues(Object.keys(obj), elementConfig.fields)) {
      //   return elementName;
      // }

      if (
        elementConfig.fields.every((field) => Object.keys(obj).includes(field))
      ) {
        return elementName;
      }
    }

    return false;
  };

  const isUiElement = (obj) => {
    return typeof getUiElementType(obj) === "string";
  };

  // final props to return
  let resultProps = reactive({});

  // handle prefix
  for (const [key, val] of Object.entries(input)) {
    let finalKey = removeInputAttributePrefix(key);
    resultProps[finalKey] = _.cloneDeep(val);
  }

  wrapUiElements(resultProps);

  // overload new props

  // todo: ui element wrapping, with a function for each element. test on dom

  // todo: input schema enforcement - prefent top level type errors.

  const mapProps = (props, map, prefix = "") => {
    let res = {};
    for (const targetKey of map) {
      let targetPropName = String(targetKey);
      res[targetKey] = props[targetPropName];
    }
    return res;
  };
  return {
    props: resultProps,
    mapProps,
    wrapUiElements,
    wrapUiElement,
    isUiElement,
  };
};
